import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { formatGpuName } from '../lib/format';
import {
  trainingService,
  Instance,
  InstanceStatus,
  GpuType,
  PricingRow,
  SshKey,
} from '../services/trainingApi';
import { LaunchPrefill } from './Gpus';
import { Page, PageHeader, ConsoleTabs, EmptyState, Notice, TableCard, cx, formatINR } from '../components/console/ui';
import InstanceStatusTag from '../components/console/InstanceStatusTag';
import { ServersIc, TrashIc, WarnIc } from '../components/console/icons';

function serverMsg(e: any, fallback: string): string {
  return e?.response?.data?.message || e?.response?.data?.error || fallback;
}

// Statuses that are still settling - while any instance is in one of these we
// keep polling the list.
const TRANSITIONAL: InstanceStatus[] = ['allocating', 'starting', 'stopping'];

export default function Instances() {
  const qc = useQueryClient();
  const location = useLocation();
  const prefill = (location.state as { prefill?: LaunchPrefill } | null)?.prefill;
  const [showCreate, setShowCreate] = useState(false);

  const instancesQ = useQuery({
    queryKey: ['instances'],
    queryFn: () => trainingService.listInstances().then((r) => r.data.data ?? []),
    // Poll while anything is still settling so newly-created instances flip to
    // running without a manual refresh.
    refetchInterval: (q) => {
      const list = (q.state.data as Instance[] | undefined) ?? [];
      return list.some((i) => TRANSITIONAL.includes(i.status)) ? 4000 : false;
    },
  });

  // Open the create form automatically when arriving from a GPUs "Launch".
  useEffect(() => {
    if (prefill) setShowCreate(true);
  }, [prefill]);

  const instances: Instance[] = instancesQ.data ?? [];

  const deleteM = useMutation({
    mutationFn: (id: string) => trainingService.deleteInstance(id),
    onSuccess: () => {
      toast.success('Termination requested');
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
    onError: (e: any) => toast.error(serverMsg(e, 'Could not terminate instance')),
  });

  const terminate = (inst: Instance) => {
    if (window.confirm(`Terminate "${inst.name || inst.id}"? This permanently destroys the instance.`)) {
      deleteM.mutate(inst.id);
    }
  };

  const connectHref = (inst: Instance) => `/console/connect?id=${encodeURIComponent(inst.id)}`;

  const terminateBtn = (inst: Instance) => (
    <button
      type="button"
      className="ps-btn-danger ps-btn-sm !px-2.5"
      disabled={inst.status === 'terminated' || deleteM.isPending}
      onClick={() => terminate(inst)}
      aria-label="Terminate instance"
      title="Terminate"
    >
      <TrashIc className="h-4 w-4" />
    </button>
  );

  const extras = (inst: Instance) => (
    <>
      {inst.billing_warning && (
        <div className="flex items-start gap-2 rounded-md bg-ps-warn-bg px-3 py-2 text-xs text-ps-warn">
          <WarnIc className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{inst.billing_warning}</span>
        </div>
      )}
      {inst.status === 'running' && (
        <div className="rounded-md border border-ps-border bg-ps-chrome px-3 py-2 text-xs text-ps-muted">
          Connect from your terminal:{' '}
          <code className="font-mono text-ps-text">podstack gpu instances ssh {inst.id}</code> -{' '}
          <Link to={connectHref(inst)} className="ps-link">
            setup guide
          </Link>
        </div>
      )}
    </>
  );

  return (
    <Page>
      <PageHeader
        title="Instances"
        subtitle="Your training GPU instances. Connect to them from your own machine with the podstack CLI."
        actions={
          <>
            <Link to="/console/connect" className="ps-btn2">
              How to connect
            </Link>
            <button type="button" className="ps-btn" onClick={() => setShowCreate(true)}>
              Create instance
            </button>
          </>
        }
      />
      <ConsoleTabs />

      {showCreate && (
        <CreateInstanceForm
          prefill={prefill}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['instances'] });
          }}
        />
      )}

      {instancesQ.isLoading ? (
        <Notice>Loading instances…</Notice>
      ) : instancesQ.isError ? (
        <Notice tone="error">Could not load instances.</Notice>
      ) : instances.length === 0 ? (
        <EmptyState
          icon={<ServersIc />}
          title="No instances yet"
          body="Create one to start training. Billing runs per hour only while it's running."
          actions={
            <>
              <button type="button" className="ps-btn" onClick={() => setShowCreate(true)}>
                Create instance
              </button>
              <Link to="/console/gpus" className="ps-btn2">
                Browse GPUs
              </Link>
            </>
          }
        />
      ) : (
        <>
          {/* Desktop / tablet table */}
          <TableCard className="hidden md:block">
            <thead>
              <tr>
                <th>Instance</th>
                <th>GPU</th>
                <th>Region</th>
                <th>Status</th>
                <th className="!text-right">Cost / hr</th>
                <th>Created</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {instances.map((inst) => {
                const hasExtras = !!inst.billing_warning || inst.status === 'running';
                return (
                  <React.Fragment key={inst.id}>
                    <tr className={cx(hasExtras && '[&>td]:!border-b-0')}>
                      <td>
                        <div className="font-medium text-ps-text">{inst.name || inst.id}</div>
                        <code className="font-mono text-[11px] text-ps-subtle">{inst.id}</code>
                      </td>
                      <td>
                        {inst.gpu_count}× {formatGpuName(inst.gpu_type)}
                        <div className="text-[11px] text-ps-subtle">{inst.tier}</div>
                      </td>
                      <td className="text-ps-muted">{inst.region}</td>
                      <td>
                        <InstanceStatusTag status={inst.status} />
                      </td>
                      <td className="text-right font-medium">{formatINR(inst.price_per_hour)}</td>
                      <td className="!whitespace-normal text-xs text-ps-muted">
                        {new Date(inst.created_at).toLocaleString()}
                        {inst.ready_at && (
                          <div className="text-[11px] text-ps-subtle">Ready {new Date(inst.ready_at).toLocaleString()}</div>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-2">
                          {inst.status === 'running' && (
                            <Link to={connectHref(inst)} className="ps-btn2 ps-btn-sm">
                              Connect
                            </Link>
                          )}
                          {terminateBtn(inst)}
                        </div>
                      </td>
                    </tr>
                    {hasExtras && (
                      <tr className="hover:[&>td]:!bg-transparent">
                        <td colSpan={7} className="!whitespace-normal !pt-0">
                          <div className="flex flex-col gap-2">{extras(inst)}</div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </TableCard>

          {/* Mobile cards */}
          <div className="flex flex-col gap-3 md:hidden">
            {instances.map((inst) => (
              <div key={inst.id} className="ps-card flex flex-col gap-3 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ps-text">{inst.name || inst.id}</span>
                      <InstanceStatusTag status={inst.status} />
                    </div>
                    <code className="block truncate font-mono text-[11px] text-ps-subtle">{inst.id}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    {inst.status === 'running' && (
                      <Link to={connectHref(inst)} className="ps-btn2 ps-btn-sm">
                        Connect
                      </Link>
                    )}
                    {terminateBtn(inst)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 border-t border-ps-raised pt-3 text-xs text-ps-muted">
                  <span>
                    {inst.gpu_count}× {formatGpuName(inst.gpu_type)}
                  </span>
                  <span>{inst.region}</span>
                  <span>{inst.tier}</span>
                  <span>{formatINR(inst.price_per_hour)}/hr</span>
                  <span>Created {new Date(inst.created_at).toLocaleString()}</span>
                  {inst.ready_at && <span>Ready {new Date(inst.ready_at).toLocaleString()}</span>}
                </div>
                {extras(inst)}
              </div>
            ))}
          </div>
        </>
      )}
    </Page>
  );
}

function CreateInstanceForm({
  prefill,
  onClose,
  onCreated,
}: {
  prefill?: LaunchPrefill;
  onClose: () => void;
  onCreated: () => void;
}) {
  const qc = useQueryClient();

  const typesQ = useQuery({
    queryKey: ['gpu-types'],
    queryFn: () => trainingService.getGpuTypes().then((r) => r.data.data ?? []),
  });
  const pricingQ = useQuery({
    queryKey: ['pricing'],
    queryFn: () => trainingService.getPricing().then((r) => r.data.data ?? []),
  });
  const keysQ = useQuery({
    queryKey: ['ssh-keys'],
    queryFn: () => trainingService.listSshKeys().then((r) => r.data.data ?? []),
  });

  const types: GpuType[] = useMemo(() => typesQ.data ?? [], [typesQ.data]);
  const pricing: PricingRow[] = useMemo(() => pricingQ.data ?? [], [pricingQ.data]);
  const keys: SshKey[] = keysQ.data ?? [];

  const [gpuType, setGpuType] = useState(prefill?.gpu_type ?? '');
  const [region, setRegion] = useState(prefill?.region ?? '');
  const [tier, setTier] = useState(prefill?.tier ?? '');
  const [gpuCount, setGpuCount] = useState(1);
  const [name, setName] = useState('');
  const [selectedKeyIds, setSelectedKeyIds] = useState<string[]>([]);

  // Paste-a-new-key sub-flow (shown when the user has no keys or wants another).
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');

  // Default the gpu_type once the catalog arrives (if not prefilled).
  useEffect(() => {
    if (!gpuType && types.length) setGpuType(types[0].gpu_type);
  }, [types, gpuType]);

  // Regions/tiers available for the chosen gpu_type, derived from pricing.
  const gpuNames = useMemo(() => {
    const set = new Set<string>(types.map((t) => t.gpu_type));
    pricing.forEach((p) => set.add(p.gpu_type));
    return Array.from(set).sort();
  }, [types, pricing]);
  const regionsForGpu = useMemo(
    () => Array.from(new Set(pricing.filter((p) => p.gpu_type === gpuType).map((p) => p.region))).sort(),
    [pricing, gpuType]
  );
  const tiersForGpu = useMemo(
    () => Array.from(new Set(pricing.filter((p) => p.gpu_type === gpuType).map((p) => p.tier))).sort(),
    [pricing, gpuType]
  );

  // Listed price for exactly this configuration, when the catalog has one.
  const listed = useMemo(
    () =>
      pricing.find(
        (p) => p.gpu_type === gpuType && p.region === region && p.tier === tier && p.gpu_count === gpuCount
      ),
    [pricing, gpuType, region, tier, gpuCount]
  );

  const createKeyM = useMutation({
    mutationFn: () => trainingService.createSshKey(newKeyName.trim(), newKeyValue.trim()).then((r) => r.data),
    onSuccess: (key) => {
      toast.success('SSH key added');
      setNewKeyName('');
      setNewKeyValue('');
      setSelectedKeyIds((ids) => [...ids, key.id]);
      qc.invalidateQueries({ queryKey: ['ssh-keys'] });
    },
    onError: (e: any) => toast.error(serverMsg(e, 'Could not add SSH key')),
  });

  const createM = useMutation({
    mutationFn: () =>
      trainingService
        .createInstance({
          gpu_type: gpuType,
          gpu_count: gpuCount,
          tier,
          region: region || undefined,
          name: name.trim() || undefined,
          ssh_key_ids: selectedKeyIds.length ? selectedKeyIds : undefined,
        })
        .then((r) => r.data),
    onSuccess: () => {
      toast.success('Instance requested - provisioning may take up to a minute');
      onCreated();
    },
    onError: (e: any) => toast.error(serverMsg(e, 'Could not create instance')),
  });

  const toggleKey = (id: string) =>
    setSelectedKeyIds((ids) => (ids.includes(id) ? ids.filter((k) => k !== id) : [...ids, id]));

  const canSubmit = !!gpuType && !!tier && gpuCount >= 1 && gpuCount <= 8 && !createM.isPending;

  const fld = 'flex flex-col gap-1.5 text-[13px]';
  const fldLabel = 'text-xs text-ps-muted';

  return (
    <section className="flex flex-col gap-4" aria-labelledby="create-instance-title">
      <div className="flex items-center justify-between gap-3">
        <h2 id="create-instance-title" className="m-0 text-[22px] font-medium text-ps-text">
          Create instance
        </h2>
        <button type="button" className="ps-link text-[13px]" onClick={onClose}>
          Cancel
        </button>
      </div>

      <div className="flex flex-wrap items-start gap-[22px]">
        <div className="flex min-w-0 flex-[3_1_560px] flex-col gap-[18px]">
          {/* GPU */}
          <section className="ps-card flex flex-col gap-3.5 p-5" aria-labelledby="create-gpu">
            <h3 id="create-gpu" className="m-0 text-base font-medium text-ps-text">
              GPU
            </h3>
            <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,200px),1fr))]">
              <label className={fld}>
                <span className={fldLabel}>GPU type</span>
                <select
                  className="ps-input"
                  value={gpuType}
                  onChange={(e) => {
                    setGpuType(e.target.value);
                    setRegion('');
                    setTier('');
                  }}
                >
                  <option value="" disabled>
                    Select a GPU
                  </option>
                  {gpuNames.map((g) => (
                    <option key={g} value={g}>
                      {formatGpuName(g)}
                    </option>
                  ))}
                </select>
              </label>

              <label className={fld}>
                <span className={fldLabel}>Region</span>
                <select className="ps-input" value={region} onChange={(e) => setRegion(e.target.value)}>
                  <option value="">Any / default</option>
                  {regionsForGpu.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>

              <label className={fld}>
                <span className={fldLabel}>Tier</span>
                <select className="ps-input" value={tier} onChange={(e) => setTier(e.target.value)}>
                  <option value="" disabled>
                    Select a tier
                  </option>
                  {tiersForGpu.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className={fld}>
              <span className={fldLabel} id="create-gpu-count">
                GPU count (1-8)
              </span>
              <div className="flex flex-wrap gap-2" role="group" aria-labelledby="create-gpu-count">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <button
                    key={n}
                    type="button"
                    aria-pressed={gpuCount === n}
                    onClick={() => setGpuCount(n)}
                    className={cx('ps-chip min-w-[52px] justify-center', gpuCount === n && 'ps-chip-on')}
                  >
                    {n}×
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Name & access */}
          <section className="ps-card flex flex-col gap-3.5 p-5" aria-labelledby="create-access">
            <h3 id="create-access" className="m-0 text-base font-medium text-ps-text">
              Name &amp; access
            </h3>
            <label className={fld}>
              <span className={fldLabel}>Name (optional)</span>
              <input
                className="ps-input"
                placeholder="e.g. llama-finetune"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            <div className={fld}>
              <span className={fldLabel}>SSH keys</span>
              {keysQ.isLoading ? (
                <div className="text-xs text-ps-subtle">Loading keys…</div>
              ) : keys.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {keys.map((k) => (
                    <label
                      key={k.id}
                      className="flex min-h-[44px] items-center gap-2.5 rounded-md border border-ps-border px-3 py-2.5 text-[13px] text-ps-text"
                    >
                      <input
                        type="checkbox"
                        className="h-[18px] w-[18px] accent-primary-500"
                        checked={selectedKeyIds.includes(k.id)}
                        onChange={() => toggleKey(k.id)}
                      />
                      <span className="flex-1 min-w-0 truncate">{k.name}</span>
                      <code className="max-w-[50%] truncate font-mono text-[11px] text-ps-subtle">{k.fingerprint}</code>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="m-0 text-xs text-ps-subtle">No SSH keys yet - paste a public key below to add one.</p>
              )}

              <div className="mt-1 flex flex-col gap-2 rounded-md border border-ps-border bg-ps-chrome p-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ps-subtle">
                  Add a new SSH public key
                </div>
                <input
                  className="ps-input"
                  placeholder="Key name (e.g. laptop)"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
                <textarea
                  className="ps-input !py-2.5 font-mono text-xs"
                  rows={3}
                  placeholder="ssh-ed25519 AAAA… user@host"
                  value={newKeyValue}
                  onChange={(e) => setNewKeyValue(e.target.value)}
                />
                <button
                  type="button"
                  className="ps-btn2 ps-btn-sm self-start"
                  disabled={!newKeyName.trim() || !newKeyValue.trim() || createKeyM.isPending}
                  onClick={() => createKeyM.mutate()}
                >
                  {createKeyM.isPending ? 'Adding…' : 'Add SSH key'}
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* Summary */}
        <aside className="ps-card flex flex-[1_1_300px] flex-col gap-3 p-5" aria-label="Summary">
          <h3 className="m-0 text-base font-medium text-ps-text">Summary</h3>
          <div className="ps-kv !border-t-0">
            <span>GPU</span>
            <span className="text-right">{gpuType ? `${gpuCount}× ${formatGpuName(gpuType)}` : '-'}</span>
          </div>
          <div className="ps-kv">
            <span>Region</span>
            <span>{region || 'Any / default'}</span>
          </div>
          <div className="ps-kv">
            <span>Tier</span>
            <span>{tier || '-'}</span>
          </div>
          {listed && (
            <div className="ps-kv items-baseline">
              <span>Price</span>
              <span>
                <span className="font-display text-[26px]">{formatINR(listed.price_per_hour)}</span>
                <span className="text-ps-subtle">/hr</span>
              </span>
            </div>
          )}
          <p className="m-0 text-xs leading-relaxed text-ps-subtle">Billed hourly from your wallet only while it runs.</p>
          <button type="button" className="ps-btn" disabled={!canSubmit} onClick={() => createM.mutate()}>
            {createM.isPending ? 'Creating…' : 'Create instance'}
          </button>
          <button type="button" className="ps-btn2" onClick={onClose}>
            Cancel
          </button>
        </aside>
      </div>
    </section>
  );
}
